/**
 * This is the file input component for the Vendor Application
 */
import { join, last, isEmpty } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormControl } from '@mui/material';
import styles from '../VendorApplication/VendorApplication.module.css';
import Button from '../ReusableComponents/Button/Button';
import LinearProgressWithLabel from '../ReusableComponents/Progress/LinearProgressWithLabel';
import { ReactComponent as TrashSVG } from '../../Assets/icons/trash.svg';
import { ReactComponent as Preview } from '../../Assets/icons/preview.svg';
import { ReactComponent as Download } from '../../Assets/icons/download.svg';
import { ReactComponent as Upload } from '../../Assets/icons/upload.svg';

class FileInput extends Component {
  constructor(props) {
    super(props);

    const { exists } = props;

    /**
     * if there has been a file uploaded for the input, we set that state on
     * render.
     */
    this.state = {
      uploaded: exists,
      error: null,
      isUploading: false,
      file: null,
    };

    this.onChange = this.onChange.bind(this);
    this.removeFile = this.removeFile.bind(this);
    this.download = this.download.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.progressEvent) {
      if (nextProps.progressEvent.loaded === nextProps.progressEvent.total) {
        this.setState({ isUploading: false });
      } else {
        this.setState({ isUploading: true });
      }
    }
  }

  /**
   * Handles the input when it is changed- i.e when the file is selected in the
   * file upload dialog box. Calls onChange from props.
   *
   * @param {object} e the input event
   */
  onChange(e) {
    const {
      target: { name, files },
    } = e;
    const { onChange, sizeLimit, fileType, selectMode } = this.props;
    const file = files[0];
    const fileSizeLimit = sizeLimit * 1000000;

    if (!fileType.includes(file.type)) {
      const error = `Only ${last(
        join(fileType).split('/'),
      )} Files are Accepted`;
      return this.setState({ error });
    }
    if (file.size > fileSizeLimit) {
      const error = `File upload cannot exceed ${sizeLimit}MB`;
      return this.setState({ error });
    }

    return this.setState(
      { uploaded: true, error: null, isUploading: !selectMode, file },
      () => onChange(name, file),
    );
  }

  /**
   * Clears the local state to allow the file to be re-uploaded. Does not
   * actually remove the file from the backend to prevent deletion without
   * re-uploading a file.
   */
  removeFile() {
    this.setState({ uploaded: false, file: null });
  }

  /**
   * If there is a link on the file input- i.e. if there is a file uploaded and a url passed to the input as a prop, open the file in a new window.
   */
  download() {
    const { defaultFileLink } = this.props;
    if (!defaultFileLink) return;
    window.open(defaultFileLink);
  }

  renderButtonLabel() {
    const { selectMode, loading } = this.props;

    if (selectMode) return 'Select';
    if (loading) return 'Uploading...';
    return 'Upload';
  }

  render() {
    const {
      disabled,
      name,
      fileLink,
      header,
      suffix,
      required,
      desc,
      defaultFileLink,
      loading,
      progressEvent,
      selectMode,
      fileType,
      textFields,
      steps,
      exists,
      perms,
    } = this.props;
    const { uploaded, error, isUploading, file } = this.state;
    const progress = progressEvent
      ? (progressEvent.loaded * 100) / progressEvent.total
      : null;
    const estimatedEnd = progressEvent
      ? ((progressEvent.total - progressEvent.loaded) *
          progressEvent.timeStamp) /
        progressEvent.loaded
      : null;

    return (
      <FormControl
        variant="standard"
        margin="dense"
        fullWidth
        required={required}
      >
        <div className={styles.uploadLabel}>
          <div className={styles.uploadLabelHeader}>
            <h4>{header}</h4>
          </div>
          <div className={styles.uploadLabelDesc}>{desc}</div>
        </div>

        <div className={styles.uploadExtras}>
          {textFields && textFields}
          {steps && steps}
        </div>

        <div className={styles.uploadButtons}>
          {perms.canRead && exists && !loading && !isUploading && (
            <a
              href={fileLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.buttonLink}
            >
              <Button
                variant="outlined"
                id="preview-button"
                color="primary"
                small
                icon
                flex
                disabled={!(uploaded && !isEmpty(fileLink))}
                className={styles.documentButton}
              >
                <Preview className={styles.iconPadLeft} />
                Preview
              </Button>
            </a>
          )}

          {defaultFileLink && (
            <Button
              variant="outlined"
              id="download-button"
              color="primary"
              onClick={this.download}
              small
              icon
              flex
              className={styles.documentButton}
            >
              <Download className={styles.iconPadLeft} />
              Download
            </Button>
          )}

          {perms.canUpload && !disabled && !uploaded && this.props.onChange && (
            <label htmlFor={`va-${suffix}`}>
              <Button
                variant="contained"
                id="upload-button"
                component="span"
                color="primary"
                small
                flex
                icon
                className={styles.documentButton}
              >
                <Upload className={styles.iconPadLeft} />
                {this.renderButtonLabel()}
              </Button>
              <input
                accept={fileType}
                id={`va-${suffix}`}
                type="file"
                name={name}
                style={{ display: 'none' }}
                onChange={this.onChange}
                disabled={disabled}
              />
            </label>
          )}

          {perms.canDelete &&
            uploaded &&
            !loading &&
            !disabled &&
            this.props.onChange &&
            !isUploading && (
              <Button
                color="destructive"
                id="delete-button"
                onClick={this.removeFile}
                small
                icon
                flex
                variant="outlined"
                className={styles.documentButton}
              >
                <TrashSVG className={styles.iconPadLeft} />
                Delete
              </Button>
            )}
          {isUploading ? (
            <LinearProgressWithLabel
              progress={progress || 0}
              estimatedEnd={estimatedEnd}
            />
          ) : null}
          {selectMode && file && !progressEvent ? (
            <span>{file.name}</span>
          ) : null}
        </div>
        <br />
        <div className={styles.error}>{error && error}</div>
      </FormControl>
    );
  }
}

FileInput.propTypes = {
  header: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  defaultFileLink: PropTypes.string,
  fileLink: PropTypes.string.isRequired,
  exists: PropTypes.bool.isRequired,
  fileType: PropTypes.arrayOf(PropTypes.string).isRequired,
  progressEvent: PropTypes.shape({
    loaded: PropTypes.number,
    total: PropTypes.number,
    timeStamp: PropTypes.number,
  }),
  selectMode: PropTypes.bool,
  onChange: PropTypes.func,
  suffix: PropTypes.string.isRequired,
  desc: PropTypes.string,
  required: PropTypes.bool,
  loading: PropTypes.bool,
  sizeLimit: PropTypes.number,
  textFields: PropTypes.node,
  steps: PropTypes.string,
  perms: PropTypes.shape({
    canUpload: PropTypes.bool,
    canDelete: PropTypes.bool,
    canRead: PropTypes.bool,
  }),
};

FileInput.defaultProps = {
  defaultFileLink: '',
  desc: '',
  required: false,
  loading: false,
  sizeLimit: 10,
  textFields: null,
  steps: null,
  progressEvent: null,
  selectMode: false,
  onChange: null,
  perms: {
    canUpload: true,
    canDelete: true,
    canRead: true,
  },
};

export default FileInput;
